import React from 'react';
import { motion } from 'framer-motion';
import { CloudIcon, CloudRainIcon, CloudSnowIcon, CloudLightningIcon, CloudFogIcon, SunIcon, CloudSunIcon } from 'lucide-react';
import { useWeather } from '../../hooks/useWeather';

export function OutdoorEnvironmentWidget({ assetId = null }: { assetId?: string | null }) {
  const { site_temperature, weatherCode, pm25, loading } = useWeather(assetId);
  
  const getWeatherInfo = (code: any) => {
    const num = code !== null && code !== undefined && !isNaN(Number(code)) ? Number(code) : null;
    if (num === null) return { Icon: SunIcon, color: 'text-amber-400', label: '맑음' };
    if (num === 0 || num === 1) return { Icon: SunIcon, color: 'text-amber-400', label: '맑음' };
    if (num === 2 || num === 3) return { Icon: CloudIcon, color: 'text-sky-300', label: '구름' };
    if (num === 45 || num === 48) return { Icon: CloudFogIcon, color: 'text-slate-300', label: '안개' };
    if ((num >= 51 && num <= 67) || (num >= 80 && num <= 82)) return { Icon: CloudRainIcon, color: 'text-cyan-400', label: '비' };
    if ((num >= 71 && num <= 77) || (num >= 85 && num <= 86)) return { Icon: CloudSnowIcon, color: 'text-sky-200', label: '눈' };
    if (num >= 95 && num <= 99) return { Icon: CloudLightningIcon, color: 'text-yellow-400', label: '뇌우' };
    
    return { Icon: SunIcon, color: 'text-amber-400', label: '맑음' };
  };

  const { Icon: WeatherIcon, color: weatherColor } = getWeatherInfo(weatherCode);

  const getPm25Status = (val: any) => {
    const num = val !== null && val !== undefined && !isNaN(Number(val)) ? Number(val) : null;
    if (num === null) return { label: '--', color: 'text-slate-500' };
    if (num <= 15) return { label: '좋음', color: 'text-emerald-400' };
    if (num <= 35) return { label: '보통', color: 'text-cyan-400' };
    if (num <= 75) return { label: '나쁨', color: 'text-amber-400' };
    return { label: '매우나쁨', color: 'text-red-400' };
  };

  const pm25Info = getPm25Status(pm25);

  if (loading && site_temperature === null) {
    return (
      <div className="w-full h-12 rounded-2xl bg-[var(--mobile-bg-card)] border border-[var(--mobile-border)] flex items-center justify-center">
        <span className="text-[0.75rem] text-cyan-400 animate-pulse">바깥날씨 로딩 중...</span>
      </div>
    );
  }

  const tempNum = site_temperature !== null && !isNaN(Number(site_temperature)) ? Number(site_temperature) : null;
  const pm25Num = pm25 !== null && !isNaN(Number(pm25)) ? Number(pm25) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
    >
      <div className="w-full px-1 py-1 flex items-center justify-between text-[var(--mobile-text-muted)]">
        {/* 좌측: 위젯 제목 '바깥날씨' (다른 섹션 제목들과 완벽한 좌측 정렬) */}
        <div className="flex items-center gap-2">
          <CloudSunIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <span className="text-[1rem] font-medium uppercase tracking-wider text-[var(--mobile-text-primary)]">
            바깥날씨
          </span>
        </div>

        {/* 우측: 날씨 상태 아이콘(비/맑음/흐림) + 기온 + 미세먼지 상태 및 수치 */}
        <div className="flex items-center gap-3">
          {/* 날씨 상태 아이콘 + 기온 */}
          <div className="flex items-center gap-1.5">
            <WeatherIcon className={`w-4 h-4 ${weatherColor} flex-shrink-0`} />
            <div className="flex items-baseline gap-0.5">
              <span className="text-base font-mono font-bold glow-text-amber text-[var(--mobile-text-primary)]">
                {tempNum !== null ? tempNum.toFixed(1) : '--'}
              </span>
              <span className="text-[0.7rem] font-mono text-[var(--mobile-text-muted)]">°C</span>
            </div>
          </div>

          <span className="text-slate-600 font-mono text-xs">·</span>

          {/* 미세먼지 등급 + 수치 */}
          <div className="flex items-center gap-1.5">
            <span className={`text-xs font-bold ${pm25Info.color}`}>
              {pm25Info.label}
            </span>
            <div className="flex items-baseline gap-0.5">
              <span className={`text-base font-mono font-bold ${pm25Info.color}`}>
                {pm25Num !== null ? Math.round(pm25Num) : '--'}
              </span>
              <span className="text-[0.65rem] font-mono text-[var(--mobile-text-muted)]">㎍</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
